import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { styled } from "styled-components";
import { Button } from "../components/common/Button";
import { Confirmation } from "../components/common/Confirmation";
import {
  ErrorMessage,
  EyeIcon,
  FormContainer,
  FormGroup,
  FormHeading,
  FormLink,
  FormText,
  Input,
  Label,
} from "../components/common/Form";
import { useAuthStore } from "../stores/auth.store";
import { DateValidation } from "../utils/dateValidation";

type Inputs = {
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  province: string;
  email: string;
  phone: string;
  birthday: string;
  password: string;
  privacyAccepted: boolean;
};

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 32px;
`;

const CheckboxInput = styled.input`
  margin-top: 4px;
`;

const PrivacyText = styled.span`
  font-size: 14px;
`;

const EyeIconButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PasswordContainer = styled.div`
  position: relative;
`;

export const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailExistingError, setEmailExistingError] = useState<string | null>(
    null,
  );

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();

  const { register: registerUser } = useAuthStore();

  const onSubmit: SubmitHandler<Inputs> = async (data, event) => {
    event?.preventDefault();
    setIsLoading(true);
    setEmailExistingError(null);

    try {
      const error = await registerUser(data);
      if (error) {
        console.error("Registration failed:", error);

        // Handle specific error cases based on status code
        if (error.status === 409) {
          setEmailExistingError(
            "Questa email è già registrata. Prova ad accedere o utilizza un'altra email.",
          );
        }

        setIsLoading(false);
        return;
      }
      setIsSubmitted(true);
    } catch (error) {
      console.error("Registration failed:", error);
      setEmailExistingError("Si è verificato un errore imprevisto. Riprova.");
      setIsLoading(false);
    }
  };

  const confirmMessage =
    " TI ABBIAMO INVIATO UNA EMAIL DI CONFERMA. SEGUI LE ISTRUZIONI PER COMPLETARE LA REGISTRAZIONE E ATTIVARE IL TUO ACCOUNT.";

  return isSubmitted ? (
    <Confirmation title="CONTROLLA LA TUA EMAIL" message={confirmMessage} />
  ) : (
    <FormContainer>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormHeading>REGISTRAZIONE</FormHeading>
        <FormText>
          PROCEDI CON LA REGISTRAZIONE DEL TUO ACCOUNT, UNA VOLTA REGISTRATO
          POTRAI PAGARE DIRETTAMENTE LA QUOTA ANNUALE E RINNOVARE LA TUA
          REGISTRAZIONE IN MODO FACILE E VELOCE. INOLTRE POTRAI INSERIRE I TUOI
          FAMIGLIARI E GESTIRE TUTTE LE ISCRIZIONI DA UN UNICO ACCOUNT.
        </FormText>

        <FormGroup>
          <Label htmlFor="first_name">Nome</Label>
          <Input
            id="first_name"
            $hasError={!!errors.firstName}
            {...register("firstName", { required: "Campo obbligatorio" })}
          />
          {errors.firstName && (
            <ErrorMessage role="alert">{errors.firstName.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="surname">Cognome</Label>
          <Input
            id="surname"
            $hasError={!!errors.lastName}
            {...register("lastName", { required: "Campo obbligatorio" })}
          />
          {errors.lastName && (
            <ErrorMessage role="alert">{errors.lastName.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="address">Indirizzo</Label>
          <Input
            id="address"
            $hasError={!!errors.address}
            {...register("address", { required: "Campo obbligatorio" })}
          />
          {errors.address && (
            <ErrorMessage role="alert">{errors.address.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="city">Città</Label>
          <Input
            id="city"
            $hasError={!!errors.city}
            {...register("city", { required: "Campo obbligatorio" })}
          />
          {errors.city && (
            <ErrorMessage role="alert">{errors.city.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="province">Provincia</Label>
          <Input
            id="province"
            $hasError={!!errors.province}
            {...register("province", { required: "Campo obbligatorio" })}
          />
          {errors.province && (
            <ErrorMessage role="alert">{errors.province.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="email">Mail</Label>
          <Input
            id="email"
            type="email"
            $hasError={!!errors.email || !!emailExistingError}
            {...register("email", {
              required: "Campo obbligatorio",
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: "Inserisci un'email valida",
              },
            })}
          />
          {errors.email && (
            <ErrorMessage role="alert">{errors.email.message}</ErrorMessage>
          )}
          {emailExistingError && (
            <ErrorMessage role="alert">{emailExistingError}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="phone">Cellulare</Label>
          <Input
            id="phone"
            type="tel"
            $hasError={!!errors.phone}
            {...register("phone", {
              required: "Campo obbligatorio",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Inserisci un numero di telefono valido",
              },
            })}
          />
          {errors.phone && (
            <ErrorMessage role="alert">{errors.phone.message}</ErrorMessage>
          )}
        </FormGroup>
        <FormGroup>
          <Label htmlFor="birthday">Data di nascita</Label>
          <Input
            id="birthday"
            type="date"
            min={DateValidation.getMinDateString()}
            max={DateValidation.getTodayString()}
            $hasError={!!errors.birthday}
            {...register("birthday", DateValidation.getAdultValidationRules())}
          />
          {errors.birthday && (
            <ErrorMessage role="alert">{errors.birthday.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="password">Seleziona una Password</Label>
          <PasswordContainer>
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              $hasError={!!errors.password}
              {...register("password", {
                required: "Campo obbligatorio",
                minLength: {
                  value: 6,
                  message: "La password deve contenere almeno 6 caratteri",
                },
                pattern: {
                  value: /^(?=.*[A-Z])(?=.*[!@#$%^&*])/,
                  message:
                    "La password deve contenere almeno una lettera maiuscola e un carattere speciale",
                },
              })}
            />
            <EyeIconButton
              type="button"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <EyeIcon isVisible={showPassword} />
            </EyeIconButton>
          </PasswordContainer>
          {errors.password && (
            <ErrorMessage role="alert">{errors.password.message}</ErrorMessage>
          )}
        </FormGroup>

        <CheckboxContainer>
          <CheckboxInput
            type="checkbox"
            id="privacyAccepted"
            {...register("privacyAccepted", {
              required: "Accettare l'informativa privacy",
            })}
          />
          <div style={{ display: "flex", flexDirection: "column" }}>
            <PrivacyText>
              acconsento al trattamento dei dati personali -{" "}
              {/* TODO: privacy policy link */}
              <FormLink
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                consulta l'informativa relativa alla privacy
              </FormLink>
            </PrivacyText>
            {errors.privacyAccepted && (
              <ErrorMessage role="alert">
                {errors.privacyAccepted.message}
              </ErrorMessage>
            )}
          </div>
        </CheckboxContainer>

        <Button
          type="submit"
          fullWidth
          loading={isLoading}
          loadingText="REGISTRAZIONE IN CORSO..."
        >
          CONFERMA REGISTRAZIONE
        </Button>
      </form>
    </FormContainer>
  );
};
